import { env } from '@/lib/env'

export type PlanFeature = string | [string, string]

export interface PlanCardProps {
  name: string
  tagline: string
  price: string
  period: string
  subPrice?: string
  ctaText: string
  ctaVariant: 'primary' | 'secondary'
  ctaDisabled: boolean
  onCta: () => void
  featuresHeading: string
  features: PlanFeature[]
  featured?: boolean
  /** Optional content rendered between the price/sub-price block and the CTA button (e.g. seat stepper). */
  extraSlot?: React.ReactNode
  /** CSS order class for responsive reordering (mobile: Pro first; desktop: Free | Pro | Team). */
  orderClass?: string
  /**
   * Visually dim the whole card to signal it is not available (e.g. a lapsed
   * trial user can't restart the Trial — PDR 011 D15, no second free trial).
   * Pair with `ctaDisabled` so the CTA is also unclickable.
   */
  dimmed?: boolean
  /** Render nothing — used to omit a card entirely (e.g. Team when billing is off). */
  hidden?: boolean
  /** Small pill rendered next to the plan name (e.g. "14-day trial"). */
  badge?: string
  /** Small line rendered under the CTA — e.g. the reason the CTA is disabled. */
  note?: string
  /** Suppress the per-card Terms/Privacy disclosure (consent handled elsewhere). */
  hideTerms?: boolean
  /** Modal-tuned density: smaller paddings, tighter feature list, no features-heading. */
  compact?: boolean
}

export function PlanCard({
  name,
  tagline,
  price,
  period,
  subPrice,
  ctaText,
  ctaVariant,
  ctaDisabled,
  onCta,
  featuresHeading,
  features,
  featured,
  extraSlot,
  orderClass,
  dimmed,
  hidden,
  badge,
  note,
  hideTerms,
  compact,
}: PlanCardProps) {
  if (hidden) return null

  const pad = compact ? 'p-4' : 'p-5 md:p-7'
  const priceSize = compact ? 'text-3xl' : 'text-4xl md:text-5xl'
  const taglineSpace = compact ? 'mb-2 min-h-[32px]' : 'mb-5 md:mb-6 md:min-h-[40px]'
  const subSpace = compact ? 'mb-3 min-h-[14px]' : 'mb-5 text-xs text-gray-500 md:mb-6 md:min-h-[20px]'

  return (
    <div
      className={[
        'relative flex flex-col ' + (compact ? 'rounded-xl' : 'rounded-2xl') + ' bg-white ' + pad,
        featured ? 'border-2 border-sky-500' : 'border border-gray-200',
        dimmed ? 'opacity-60' : '',
        orderClass ?? '',
      ].join(' ')}
    >
      <div
        className={[
          'mb-1 inline-flex h-7 items-center self-start text-xs font-medium uppercase tracking-wider',
          featured
            ? 'rounded-md bg-sky-500 px-2.5 text-white tracking-[0.12em]'
            : 'text-gray-900',
        ].join(' ')}
      >
        {name}
        {badge ? (
          <span className="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">{badge}</span>
        ) : null}
      </div>
      <p className={'text-sm text-gray-600 ' + taglineSpace}>{tagline}</p>

      <div className="mb-1 flex items-baseline gap-1.5">
        <span className={'font-medium tracking-tight text-gray-900 ' + priceSize}>{price}</span>
        <span className="text-sm text-gray-600">{period}</span>
      </div>
      <p className={'text-xs ' + subSpace}>{subPrice ?? ''}</p>

      {extraSlot}

      <button
        type="button"
        onClick={onCta}
        disabled={ctaDisabled}
        className={[
          'mb-2 block w-full rounded-lg px-4 py-3 text-center text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
          ctaVariant === 'primary'
            ? 'bg-sky-500 text-white hover:bg-sky-600'
            : 'border border-gray-300 text-gray-900 hover:border-gray-900',
        ].join(' ')}
      >
        {ctaText}
      </button>
      {note ? <p className="mt-1 mb-2 text-center text-[11px] leading-snug text-amber-700">{note}</p> : null}
      {!hideTerms && (
        <p className="mb-6 text-center text-[11px] leading-snug text-gray-500">
          By continuing, you agree to our{' '}
          <a
            href={env.termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 hover:underline"
          >
            Terms
          </a>{' '}
          and{' '}
          <a
            href={env.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-600 hover:underline"
          >
            Privacy Policy
          </a>
          .
        </p>
      )}

      {compact ? (
        <ul className="mt-3 flex flex-col gap-1.5">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
              <span className="mt-0.5 text-sky-500">✓</span>
              <span>{Array.isArray(f) ? (<><strong className="font-medium text-gray-800">{f[0]}</strong> {f[1]}</>) : f}</span>
            </li>
          ))}
        </ul>
      ) : (
        <>
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500">
            {featuresHeading}
          </p>
          <ul className="flex flex-col gap-3">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm leading-snug text-gray-900">
                <svg
                  className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-sky-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>
                  {Array.isArray(f) ? (
                    <>
                      <strong className="font-semibold">{f[0]}</strong> {f[1]}
                    </>
                  ) : (
                    f
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
